package screens.admin

import androidx.compose.runtime.*
import com.example.readingnotes.di.AppModule
import com.example.readingnotes.presentation.sales.SalesDashboardEvent
import com.example.readingnotes.presentation.sales.SalesDashboardViewModel
import com.example.readingnotes.presentation.sales.SalesPeriod
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import styles.AppStyleSheet

private fun formatYen(amount: Int): String {
    @Suppress("UnsafeDynamicallyTypedCall")
    val formatted = js("Number(amount).toLocaleString('ja-JP')") as String
    return "¥$formatted"
}

@Composable
fun SalesDashboardScreen(
    onBackClick: () -> Unit,
    appModule: AppModule,
    appScope: CoroutineScope,
) {
    val viewModel =
        remember {
            SalesDashboardViewModel(
                getSalesDashboardUseCase = appModule.getSalesDashboardUseCase,
                viewModelScope = appScope,
            )
        }
    val state by viewModel.state.collectAsState()

    // Container div with proper styling
    Div({
        classes(AppStyleSheet.container)
        style {
            property("max-width", "1000px")
            property("margin", "0 auto")
            padding(20.px)
        }
    }) {
        // Header with back button
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.SpaceBetween)
                marginBottom(24.px)
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                }
            }) {
                Button({
                    classes(AppStyleSheet.backButton)
                    onClick { onBackClick() }
                    style {
                        marginRight(16.px)
                    }
                }) { Text("← 戻る") }

                H2({
                    style {
                        margin(0.px)
                    }
                }) {
                    Text("販売ダッシュボード")
                }
            }

            // Refresh button
            Button({
                classes(AppStyleSheet.secondaryButton)
                onClick {
                    viewModel.onEvent(SalesDashboardEvent.OnRefresh)
                }
                if (state.isRefreshing) {
                    attr("disabled", "true")
                }
            }) {
                Text(if (state.isRefreshing) "更新中..." else "更新")
            }
        }

        // Main content
        when {
            state.isLoading -> {
                Div({
                    classes(AppStyleSheet.loading)
                    attr("role", "status")
                    attr("aria-live", "polite")
                    attr("aria-label", "読み込み中")
                }) {
                    Div({ classes(AppStyleSheet.loadingSpinner) }) {}
                    Text("読み込み中...")
                }
            }

            state.errorMessage != null -> {
                Div({
                    classes(AppStyleSheet.formError)
                    style {
                        marginBottom(16.px)
                    }
                }) {
                    Text(state.errorMessage ?: "エラーが発生しました")
                }
                Button({
                    classes(AppStyleSheet.primaryButton)
                    onClick {
                        viewModel.onEvent(SalesDashboardEvent.OnClearError)
                        viewModel.onEvent(SalesDashboardEvent.OnRefresh)
                    }
                }) {
                    Text("再試行")
                }
            }

            state.dashboard != null -> {
                val dashboard = state.dashboard!!
                DashboardContent(
                    dashboard = dashboard,
                    selectedPeriod = state.selectedPeriod,
                    onPeriodChange = { period ->
                        viewModel.onEvent(SalesDashboardEvent.OnPeriodChange(period))
                    },
                )
            }
        }
    }
}

@Composable
private fun DashboardContent(
    dashboard: com.example.readingnotes.domain.model.SalesDashboard,
    selectedPeriod: SalesPeriod,
    onPeriodChange: (SalesPeriod) -> Unit,
) {
    // Period selector
    Div({
        style {
            marginBottom(24.px)
        }
    }) {
        Span({
            style {
                fontWeight(500)
                property("margin-bottom", "8px")
                property("display", "block")
            }
        }) {
            Text("期間")
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                gap(8.px)
                flexWrap(FlexWrap.Wrap)
            }
        }) {
            SalesPeriod.entries.forEach { period ->
                val isSelected = selectedPeriod == period
                Button({
                    classes(
                        if (isSelected) AppStyleSheet.primaryButton
                        else AppStyleSheet.secondaryButton,
                    )
                    onClick { onPeriodChange(period) }
                    style {
                        if (!isSelected) {
                            property("background-color", "#f3f4f6")
                            property("color", "#374151")
                            property("border", "1px solid #d1d5db")
                        }
                    }
                }) {
                    Text(
                        when (period) {
                            SalesPeriod.ALL -> "すべて"
                            SalesPeriod.THIS_MONTH -> "今月"
                            SalesPeriod.THIS_WEEK -> "今週"
                            SalesPeriod.TODAY -> "今日"
                        },
                    )
                }
            }
        }
    }

    // Summary cards
    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "repeat(auto-fit, minmax(200px, 1fr))")
            gap(16.px)
            marginBottom(24.px)
        }
    }) {
        SummaryCard("総販売数", dashboard.totalSales.toString())
        SummaryCard("総収益", formatYen(dashboard.totalRevenue))
        SummaryCard("あなたの収益", formatYen(dashboard.creatorEarnings))
    }

    // Platform fees
    Div({
        style {
            property("background-color", "#f3f4f6")
            property("border-radius", "8px")
            property("padding", "16px")
            marginBottom(24.px)
        }
    }) {
        Span({
            style {
                property("font-size", "14px")
                property("color", "#6B7280")
                property("margin-bottom", "4px")
                property("display", "block")
            }
        }) {
            Text("プラットフォーム手数料")
        }
        Span({
            style {
                property("font-size", "24px")
                property("font-weight", "bold")
                property("color", "#6B7280")
            }
        }) {
            Text(formatYen(dashboard.platformFees))
        }
    }

    // Sales by content section
    H3({
        style {
            property("margin", "0 0 16.px 0")
            property("font-size", "18px")
        }
    }) {
        Text("記事別の販売")
    }

    if (dashboard.salesByContent.isEmpty()) {
        Div({
            style {
                property("background-color", "#f9fafb")
                property("border-radius", "8px")
                property("padding", "24.px")
                property("text-align", "center")
                property("color", "#6B7280")
            }
        }) {
            Text("販売データがありません")
        }
    } else {
        // Find max sales for progress bar scaling
        val maxSales = dashboard.salesByContent.maxOfOrNull { it.salesCount } ?: 1

        dashboard.salesByContent.forEach { sale ->
            ContentSalesCard(sale, maxSales)
        }
    }
}

@Composable
private fun SummaryCard(
    label: String,
    value: String,
) {
    Div({
        style {
            property("background-color", "#DBEAFE")
            property("border-radius", "8px")
            property("padding", "16px")
            property("text-align", "center")
        }
    }) {
        Div({
            style {
                property("font-size", "12px")
                property("color", "#1E3A8A")
                marginBottom(4.px)
            }
        }) {
            Text(label)
        }
        Div({
            style {
                property("font-size", "24px")
                property("font-weight", "bold")
                property("color", "#1E3A8A")
            }
        }) {
            Text(value)
        }
    }
}

@Composable
private fun ContentSalesCard(
    sale: com.example.readingnotes.domain.model.ContentSales,
    maxSales: Int,
) {
    Div({
        style {
            property("background-color", "#ffffff")
            property("border-radius", "8px")
            property("padding", "16px")
            property("margin-bottom", "12.px")
            property("box-shadow", "0 1px 3px rgba(0,0,0,0.1)")
        }
    }) {
        H4({
            style {
                property("margin", "0 0 8.px 0")
                property("font-size", "16px")
            }
        }) {
            Text(sale.title)
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                marginBottom(8.px)
            }
        }) {
            Span({
                style {
                    property("font-size", "14px")
                    property("color", "#6B7280")
                }
            }) {
                Text("販売数: ${sale.salesCount}")
            }
            Span({
                style {
                    property("font-size", "14px")
                    property("color", "#6B7280")
                }
            }) {
                Text("収益: ${formatYen(sale.revenue)}")
            }
        }

        // Progress bar
        val progressPercent = (sale.salesCount.toFloat() / maxSales.toFloat() * 100).toInt()
        Div({
            style {
                property("width", "100%")
                property("height", "8px")
                property("background-color", "#E5E7EB")
                property("border-radius", "4px")
                property("overflow", "hidden")
            }
        }) {
            Div({
                style {
                    property("width", "$progressPercent%")
                    property("height", "100%")
                    property("background-color", "#2563EB")
                }
            }) {}
        }
    }
}
